import { Assets, Rectangle, Texture } from 'pixi.js';
import { decode as cborDecode } from 'cbor-x';
import type { SongEntry } from '../data/types';
import { withBase } from './paths';

/** Audio URLs in songs.json are stored as `$<filename>` to dedupe the long shared prefix.
 *  Expand to the CORS-fronting Cloudflare Worker that proxies r2.e-z.host with the same path. */
const AUDIO_RUNTIME_BASE = 'https://ubeat-audio.oein0219.workers.dev/a13b6f5c-3520-41a2-ba6d-5911d7d2674b/';
function expandAudioUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  if (u.startsWith('$')) return AUDIO_RUNTIME_BASE + u.slice(1);
  return u;
}

let songsPromise: Promise<SongEntry[]> | null = null;
let sfxPromise: Promise<Record<string, string | null>> | null = null;
let markersPromise: Promise<MarkerSpec[]> | null = null;

export interface MarkerSpec {
  id: string;
  files: {
    banner: string;
    approach: { folder: string; frames: string[] };
    h1?: { folder: string; frames: string[] };
    h2?: { folder: string; frames: string[] };
    h3?: { folder: string; frames: string[] };
    h4?: { folder: string; frames: string[] };
    mini?: { folder: string; frames: string[] };
  };
  baseUrl: string;
}

export async function loadSongs(): Promise<SongEntry[]> {
  if (!songsPromise) {
    // Primary: songs.cbor (smaller raw + faster to parse than JSON).
    // Falls back to songs.json for any old client/SW cache that still serves it.
    songsPromise = (async () => {
      const cborUrl = withBase('/data/songs.cbor');
      try {
        const r = await fetch(cborUrl);
        if (r.ok) {
          const buf = new Uint8Array(await r.arrayBuffer());
          const list = cborDecode(buf) as SongEntry[];
          for (const s of list) {
            s.paths.audio = expandAudioUrl(s.paths.audio) ?? s.paths.audio;
            s.paths.preview = expandAudioUrl(s.paths.preview);
          }
          return list;
        }
      } catch {}
      // Fallback path.
      const r = await fetch(withBase('/data/songs.json'));
      if (!r.ok) throw new Error('songs catalog missing — run `npm run data:songs`');
      const list = (await r.json()) as SongEntry[];
      for (const s of list) {
        s.paths.audio = expandAudioUrl(s.paths.audio) ?? s.paths.audio;
        s.paths.preview = expandAudioUrl(s.paths.preview);
      }
      return list;
    })();
  }
  return songsPromise;
}

export async function loadSfxMap(): Promise<Record<string, string | null>> {
  if (!sfxPromise) {
    sfxPromise = fetch(withBase('/data/sfx.json')).then(r => {
      if (!r.ok) throw new Error('sfx.json missing — run `npm run data:sfx`');
      return r.json();
    }).catch(() => ({}));
  }
  return sfxPromise;
}

export async function loadMarkers(): Promise<MarkerSpec[]> {
  if (!markersPromise) {
    markersPromise = (async () => {
      const r = await fetch(withBase('/dataset/markers/markers.json'));
      if (!r.ok) throw new Error('markers.json missing');
      const data = await r.json();
      const list: MarkerSpec[] = [];
      for (const m of data.markers ?? []) {
        if (m.status !== 'ok') continue;
        const baseUrl = withBase(`/dataset/markers/${m.id}/`);
        list.push({ id: m.id, files: m.files, baseUrl });
      }
      return list;
    })();
  }
  return markersPromise;
}

export async function loadMarkerFrames(spec: MarkerSpec, key: 'approach' | 'h1' | 'h2' | 'h3' | 'h4'): Promise<Texture[]> {
  const sub = spec.files[key];
  if (!sub) return [];
  const urls = sub.frames.map(f => `${spec.baseUrl}${sub.folder}/${f}`);
  return await Promise.all(urls.map(u => Assets.load<Texture>(u)));
}

export async function loadChart(url: string): Promise<any> {
  const u = withBase(url);
  const r = await fetch(u);
  if (!r.ok) throw new Error(`chart fetch ${u}: ${r.status}`);
  // Charts in the published dataset are CBOR-encoded memon (saves ~80% over JSON; see
  // scripts/sync-musicpack-static.mjs). Fall back to JSON parsing for any stray .memon URLs.
  if (/\.cbor$/i.test(u)) {
    const buf = new Uint8Array(await r.arrayBuffer());
    return cborDecode(buf);
  }
  return await r.json();
}

// ---------- long-note (hold) marker assets ----------
// Sprite sheets are row-major grids with per-frame size = sheet/rows × sheet/cols. Slice once into
// reusable sub-Texture views so the GPU upload happens a single time.

export interface LongNoteAssets {
  fps: number;
  size: number; // logical cell size (e.g. 160)
  note: { background: Texture[]; outline: Texture[]; highlight: Texture[] };
  tail: Texture[];
  tip: { appearance: Texture[]; enterCycle: Texture[]; cycle: Texture[] };
}

let longNotePromise: Promise<LongNoteAssets> | null = null;

function sliceSheet(tex: Texture, cols: number, rows: number, count: number): Texture[] {
  const frameW = tex.width / cols;
  const frameH = tex.height / rows;
  const out: Texture[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    out.push(new Texture({
      source: tex.source,
      frame: new Rectangle(col * frameW, row * frameH, frameW, frameH),
    }));
  }
  return out;
}

export async function loadLongNoteAssets(): Promise<LongNoteAssets> {
  if (longNotePromise) return longNotePromise;
  longNotePromise = (async () => {
    const baseUrl = withBase('/dataset/markers/long/classic/');
    const metaRes = await fetch(baseUrl + 'long.json');
    if (!metaRes.ok) throw new Error('long.json missing');
    const meta = await metaRes.json() as {
      fps: number; size: number;
      note: Record<'background' | 'outline' | 'highlight', { sprite_sheet: string; count: number; columns: number; rows: number }>;
      tail: { sprite_sheet: string; count: number; columns: number; rows: number };
      tip: Record<'appearance' | 'enter cycle' | 'cycle', { sprite_sheet: string; count: number; columns: number; rows: number }>;
    };
    const load = async (spec: { sprite_sheet: string; count: number; columns: number; rows: number }) => {
      const tex = await Assets.load<Texture>(baseUrl + spec.sprite_sheet);
      return sliceSheet(tex, spec.columns, spec.rows, spec.count);
    };
    const [bg, outline, hl, tail, appear, enterCyc, cyc] = await Promise.all([
      load(meta.note.background),
      load(meta.note.outline),
      load(meta.note.highlight),
      load(meta.tail),
      load(meta.tip.appearance),
      load(meta.tip['enter cycle']),
      load(meta.tip.cycle),
    ]);
    return {
      fps: meta.fps,
      size: meta.size,
      note: { background: bg, outline, highlight: hl },
      tail,
      tip: { appearance: appear, enterCycle: enterCyc, cycle: cyc },
    };
  })();
  return longNotePromise;
}
